import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, InputAdornment,
  Grid, MenuItem, Select, FormControl, InputLabel, Tooltip, CircularProgress,
  TablePagination, Menu, ListItemIcon, ListItemText
} from "@mui/material";
import {
  Search, PictureAsPdf, Code, Cancel, Refresh, FilterList, Replay, Delete, Email, Print, Receipt
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "";

const FacturasList = () => {
  const [facturas, setFacturas] = useState([]);
  const [facturasFiltradas, setFacturasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reintentando, setReintentando] = useState({});
  
  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Menu de impresión
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printMenuFactura, setPrintMenuFactura] = useState(null);
  
  // Filtros
  const [busquedaGeneral, setBusquedaGeneral] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [filtroDia, setFiltroDia] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Cargar facturas desde el backend (filtradas por empresa en backend)
  useEffect(() => {
    fetchFacturas();
  }, []);

  const fetchFacturas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/facturas`, {
        headers: getAuthHeaders(),
      });
      // Ordenar por fecha más reciente y tomar las últimas 20
      const facturasOrdenadas = (res.data || [])
        .sort((a, b) => new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0));
      setFacturas(facturasOrdenadas);
      setFacturasFiltradas(facturasOrdenadas);
    } catch (err) {
      console.error("Error al cargar facturas:", err);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  useEffect(() => {
    let resultado = [...facturas];

    // Filtro de búsqueda general (cliente, RUC, clave de acceso)
    if (busquedaGeneral) {
      const busqueda = busquedaGeneral.toLowerCase();
      resultado = resultado.filter(f => 
        f.cliente?.nombre?.toLowerCase().includes(busqueda) ||
        f.cliente?.ruc?.includes(busqueda) ||
        f.claveAcceso?.includes(busqueda) ||
        f.numeroAutorizacion?.includes(busqueda)
      );
    }

    // Filtro por número de factura (secuencial)
    if (numeroFactura) {
      resultado = resultado.filter(f => 
        f.secuencial?.toString().includes(numeroFactura)
      );
    }

    // Filtro por día
    if (filtroDia) {
      resultado = resultado.filter(f => {
        const fecha = f.fechaEmision?.split("/");
        return fecha && fecha[0] === String(filtroDia).padStart(2, "0");
      });
    }

    // Filtro por mes
    if (filtroMes) {
      resultado = resultado.filter(f => {
        const fecha = f.fechaEmision?.split("/");
        return fecha && fecha[1] === String(filtroMes).padStart(2, "0");
      });
    }

    // Filtro por año
    if (filtroAnio) {
      resultado = resultado.filter(f => {
        const fecha = f.fechaEmision?.split("/");
        return fecha && fecha[2] === String(filtroAnio);
      });
    }

    setFacturasFiltradas(resultado);
    setPage(0);
  }, [busquedaGeneral, numeroFactura, filtroDia, filtroMes, filtroAnio, facturas]);

  const limpiarFiltros = () => {
    setBusquedaGeneral("");
    setNumeroFactura("");
    setFiltroDia("");
    setFiltroMes("");
    setFiltroAnio("");
  };

  const handleVerArchivo = (filePath) => {
    if (!filePath) return alert("Archivo no disponible");
    let relativePath = filePath;
    const match = filePath.match(/(facturas\/|facturas_pdf\/)/);
    if (match) {
      relativePath = filePath.substring(filePath.indexOf(match[0]));
    }
    const url = `${API_URL}/${relativePath}`;
    window.open(url, "_blank");
  };

  const imprimirReciboFactura = (factura) => {
    const configImp = JSON.parse(localStorage.getItem('config_impresora') || '{}');
    const tipo = configImp.tipo_ticket || '58mm';
    const ancho = tipo === '58mm' ? 32 : 48;
    const linea = '='.repeat(ancho);
    const lineaFina = '-'.repeat(ancho);
    const centrar = (t) => { const p = Math.max(0, Math.floor((ancho - t.length) / 2)); return ' '.repeat(p) + t; };
    const derecha = (lab, val) => `${lab}${' '.repeat(Math.max(1, ancho - lab.length - val.length))}${val}`;

    const items = (factura.detalles || []).map(d => {
      const nombre = (d.nombre || d.descripcion || '').substring(0, ancho - 2);
      const cant = d.cantidad || 1;
      const precio = parseFloat(d.precio || d.precioUnitario || 0);
      const totalItem = (cant * precio).toFixed(2);
      return `${nombre}\n  ${cant} x $${precio.toFixed(2)}${' '.repeat(Math.max(1, ancho - 8 - cant.toString().length - precio.toFixed(2).length - totalItem.length))}$${totalItem}`;
    }).join('\n');

    const secFmt = factura.secuencial ? String(factura.secuencial).padStart(9, '0') : '';
    const totalVal = parseFloat(factura.totales?.valorPagar || factura.totales?.importeTotal || 0);
    const subtotal = parseFloat(factura.totales?.subtotalSinImpuestos || factura.totales?.subtotal15 || 0) + parseFloat(factura.totales?.subtotal0 || 0);
    const ivaVal = parseFloat(factura.totales?.iva15 || 0);
    const claveAcceso = factura.claveAcceso || '';

    const ticket = [
      centrar(configImp.nombre_empresa_ticket || 'EMPRESA'),
      linea,
      centrar('FACTURA ELECTRONICA'),
      secFmt ? centrar(`No: ${secFmt}`) : '',
      centrar(factura.fechaEmision || ''),
      linea,
      `Cliente: ${factura.cliente?.nombre || 'CONSUMIDOR FINAL'}`,
      factura.cliente?.ruc && factura.cliente.ruc !== '9999999999999' ? `CI/RUC:  ${factura.cliente.ruc}` : '',
      factura.cliente?.direccion && factura.cliente.direccion !== 'S/N' ? `Dir:     ${factura.cliente.direccion}` : '',
      factura.cliente?.email ? `Email:   ${factura.cliente.email}` : '',
      lineaFina,
      items,
      lineaFina,
      derecha('SUBTOTAL:', `$${subtotal.toFixed(2)}`),
      derecha('IVA:', `$${ivaVal.toFixed(2)}`),
      linea,
      derecha('TOTAL:', `$${totalVal.toFixed(2)}`),
      linea,
      claveAcceso ? 'Clave de Acceso:' : '',
      claveAcceso ? claveAcceso : '',
      '',
      centrar(configImp.mensaje_pie_ticket || 'Gracias por su compra'),
      '',
    ].filter(Boolean).join('\n');

    const ventana = window.open('', '_blank', `width=${tipo === '58mm' ? 220 : 302},height=600`);
    if (!ventana) return;
    ventana.document.write(`<!DOCTYPE html><html><head><title>Ticket</title>
      <style>@page{margin:0;size:${tipo} auto;}*{margin:0;padding:0;}
      body{font-family:'Courier New',monospace;font-size:${tipo === '58mm' ? '9px' : '11px'};padding:${configImp.margen_ticket || 3}px;width:${tipo === '58mm' ? '48mm' : '72mm'};}
      pre{white-space:pre-wrap;word-wrap:break-word;}</style></head>
      <body><pre>${ticket}</pre>
      <script>window.onload=function(){window.print();setTimeout(function(){window.close()},1500);}</script>
    </body></html>`);
    ventana.document.close();
  };

  const handleAnularFactura = async (claveAcceso) => {
    if (!window.confirm("¿Seguro que deseas anular esta factura?")) return;

    try {
      const res = await axios.post(`${API_URL}/api/anular/${claveAcceso}`, {}, {
        headers: getAuthHeaders(),
      });
      alert(res.data.message || "Factura anulada correctamente");

      const updated = facturas.map((f) =>
        f.claveAcceso === claveAcceso ? { ...f, estadoSRI: "ANULADA" } : f
      );
      setFacturas(updated);
    } catch (err) {
      console.error("Error al anular factura:", err);
      alert(err.response?.data?.error || "Error al anular la factura");
    }
  };

  const handleEliminarFactura = async (claveAcceso, estado) => {
    if (estado === "AUTORIZADO") {
      alert("No se puede eliminar una factura autorizada por el SRI.");
      return;
    }
    if (!window.confirm(`¿Eliminar esta factura (${estado})? Esta acción no se puede deshacer.`)) return;

    try {
      await axios.delete(`${API_URL}/api/facturas/${claveAcceso}`, {
        headers: getAuthHeaders(),
      });
      alert("Factura eliminada correctamente");
      setFacturas(facturas.filter(f => f.claveAcceso !== claveAcceso));
    } catch (err) {
      console.error("Error al eliminar factura:", err);
      alert(err.response?.data?.error || "Error al eliminar la factura");
    }
  };

  const handleEnviarEmail = async (factura) => {
    const email = factura.cliente?.email || prompt("Ingrese el email del cliente:");
    if (!email) return;

    try {
      const res = await axios.post(`${API_URL}/api/email/enviar-factura`, {
        claveAcceso: factura.claveAcceso,
        destinatario: email,
        secuencial: factura.secuencial,
        total: factura.totales?.valorPagar || factura.totales?.importeTotal,
      }, { headers: getAuthHeaders() });
      alert(`✅ Factura enviada a ${email}`);
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Error al enviar email";
      if (errorMsg.includes("SMTP") || errorMsg.includes("Configure")) {
        alert(`❌ ${errorMsg}\n\nVaya a Configuración → Email para configurar su correo SMTP.`);
      } else {
        alert(`❌ ${errorMsg}`);
      }
    }
  };

  const getEstadoChip = (estado) => {
    const estados = {
      AUTORIZADO: { color: "success", label: "AUTORIZADO" },
      PENDIENTE: { color: "warning", label: "PENDIENTE" },
      RECHAZADO: { color: "error", label: "RECHAZADO" },
      NO_AUTORIZADO: { color: "error", label: "NO AUTORIZADO" },
      ERROR_AUTORIZACION: { color: "error", label: "ERROR" },
      ANULADA: { color: "default", label: "ANULADA" },
    };
    const config = estados[estado] || { color: "default", label: estado };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleReintentarAutorizacion = async (claveAcceso) => {
    setReintentando(prev => ({ ...prev, [claveAcceso]: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/reintentar-autorizacion/${claveAcceso}`,
        {},
        { headers: getAuthHeaders() }
      );

      const { estado, mensaje } = res.data;

      if (estado === "AUTORIZADO") {
        alert("✅ Factura AUTORIZADA por el SRI");
        // Actualizar estado local
        setFacturas(prev =>
          prev.map(f =>
            f.claveAcceso === claveAcceso ? { ...f, estadoSRI: "AUTORIZADO" } : f
          )
        );
      } else if (estado === "PENDIENTE") {
        alert("⏳ " + mensaje);
        // Refrescar después de unos segundos para ver si cambió
        setTimeout(fetchFacturas, 15000);
      } else {
        alert("❌ " + (mensaje || `Estado: ${estado}`));
        setFacturas(prev =>
          prev.map(f =>
            f.claveAcceso === claveAcceso ? { ...f, estadoSRI: estado } : f
          )
        );
      }
    } catch (err) {
      console.error("Error reintentando autorización:", err);
      alert(err.response?.data?.error || "Error al reintentar autorización");
    } finally {
      setReintentando(prev => ({ ...prev, [claveAcceso]: false }));
    }
  };

  // Obtener años únicos de las facturas
  const aniosDisponibles = [...new Set(facturas.map(f => {
    const fecha = f.fechaEmision?.split("/");
    return fecha ? fecha[2] : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>
            Facturas Generadas
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchFacturas}
          >
            Actualizar
          </Button>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FilterList />
            <Typography variant="subtitle1" fontWeight={600}>
              Filtros de Búsqueda
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Búsqueda General"
                placeholder="Cliente, RUC, Clave de Acceso..."
                value={busquedaGeneral}
                onChange={(e) => setBusquedaGeneral(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="No. Factura"
                placeholder="Secuencial"
                value={numeroFactura}
                onChange={(e) => setNumeroFactura(e.target.value)}
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Día"
                placeholder="1-31"
                value={filtroDia}
                onChange={(e) => setFiltroDia(e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 31 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mes</InputLabel>
                <Select
                  value={filtroMes}
                  label="Mes"
                  onChange={(e) => setFiltroMes(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="1">Enero</MenuItem>
                  <MenuItem value="2">Febrero</MenuItem>
                  <MenuItem value="3">Marzo</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Mayo</MenuItem>
                  <MenuItem value="6">Junio</MenuItem>
                  <MenuItem value="7">Julio</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Septiembre</MenuItem>
                  <MenuItem value="10">Octubre</MenuItem>
                  <MenuItem value="11">Noviembre</MenuItem>
                  <MenuItem value="12">Diciembre</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Año</InputLabel>
                <Select
                  value={filtroAnio}
                  label="Año"
                  onChange={(e) => setFiltroAnio(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {aniosDisponibles.map(anio => (
                    <MenuItem key={anio} value={anio}>{anio}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={limpiarFiltros}
            >
              Limpiar Filtros
            </Button>
          </Box>
        </Paper>

        {/* Tabla de facturas */}
        {facturasFiltradas.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              No hay facturas que coincidan con los filtros.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>No.</strong></TableCell>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Cliente</strong></TableCell>
                  <TableCell><strong>RUC/CI</strong></TableCell>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell><strong>Autorización</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facturasFiltradas
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((f) => (
                  <TableRow key={f.claveAcceso} hover>
                    <TableCell>
                      {String(f.secuencial || "").padStart(9, "0")}
                    </TableCell>
                    <TableCell>{f.fechaEmision}</TableCell>
                    <TableCell>{f.cliente?.nombre || "—"}</TableCell>
                    <TableCell>{f.cliente?.ruc || "—"}</TableCell>
                    <TableCell>
                      <strong>${parseFloat(f.totales?.valorPagar || 0).toFixed(2)}</strong>
                    </TableCell>
                    <TableCell>{getEstadoChip(f.estadoSRI)}</TableCell>
                    <TableCell>
                      <Tooltip title={f.claveAcceso || "Sin autorización"}>
                        <Typography variant="caption" sx={{ cursor: "pointer" }}>
                          {f.claveAcceso ? `${f.claveAcceso.substring(0, 15)}...` : "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                        <Tooltip title="Ver PDF">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleVerArchivo(f.pdfPath)}
                              disabled={!f.pdfPath}
                            >
                              <PictureAsPdf />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Imprimir">
                          <span>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={(e) => { setPrintMenuAnchor(e.currentTarget); setPrintMenuFactura(f); }}
                          >
                            <Print />
                          </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Enviar por Email">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleEnviarEmail(f)}
                            disabled={f.estadoSRI !== "AUTORIZADO"}
                          >
                            <Email />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Anular Factura">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleAnularFactura(f.claveAcceso)}
                              disabled={f.estadoSRI === "ANULADA"}
                            >
                              <Cancel />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {["PENDIENTE", "ERROR_AUTORIZACION", "RECHAZADO"].includes(f.estadoSRI) && (
                          <Tooltip title="Reintentar autorización SRI">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleReintentarAutorizacion(f.claveAcceso)}
                                disabled={reintentando[f.claveAcceso]}
                              >
                                {reintentando[f.claveAcceso] ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <Replay />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {["ANULADA", "RECHAZADO", "DEVUELTO", "NO_AUTORIZADO", "ERROR_AUTORIZACION"].includes(f.estadoSRI) && (
                          <Tooltip title="Eliminar factura">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleEliminarFactura(f.claveAcceso, f.estadoSRI)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={facturasFiltradas.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Facturas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Menu popup de impresión */}
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={() => { setPrintMenuAnchor(null); setPrintMenuFactura(null); }}
      >
        <MenuItem onClick={() => {
          if (printMenuFactura) imprimirReciboFactura(printMenuFactura);
          setPrintMenuAnchor(null); setPrintMenuFactura(null);
        }} disabled={!printMenuFactura?.detalles?.length}>
          <ListItemIcon><Receipt fontSize="small" /></ListItemIcon>
          <ListItemText>Imprimir Recibo (Ticket)</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (printMenuFactura) handleVerArchivo(printMenuFactura.pdfPath || `facturas_pdf/factura_${printMenuFactura.claveAcceso}.pdf`);
          setPrintMenuAnchor(null); setPrintMenuFactura(null);
        }}>
          <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
          <ListItemText>Imprimir PDF (A4)</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FacturasList;
